import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { toast } from "sonner";
import { Mail, Lock, User } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("Se o e-mail existir, enviamos um link de recuperação. Verifique sua caixa de entrada.");
        setMode("login");
        return;
      }
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(endpoint, payload);
      loginWithToken(data.token, data.user);
      toast.success(mode === "login" ? "Bem-vindo de volta!" : "Conta criada com sucesso!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao autenticar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-primary p-12 text-primary-foreground">
        <div className="flex items-center gap-2">
          <img src="/np-logo.png" alt="NotaHoje" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" />
          <span className="font-display font-extrabold text-xl">NotaHoje</span>
        </div>
        <div>
          <h2 className="font-display font-extrabold text-4xl leading-tight">Seu serviço. Seu cliente. Seu documento.</h2>
          <p className="mt-4 text-primary-foreground/80 text-lg">Orçamentos, ordens de serviço e laudos técnicos em PDF, com assinatura digital e envio pelo WhatsApp.</p>
        </div>
        <p className="text-primary-foreground/70 text-sm">Feito para assistências técnicas, salões, oficinas e prestadores de serviço.</p>
      </div>

      <div className="flex items-center justify-center p-6 relative">
        <div className="absolute top-4 right-4"><ThemeToggle /></div>
        <div className="w-full max-w-md">
          <h1 className="font-display font-extrabold text-3xl">{mode === "forgot" ? "Recuperar senha" : mode === "login" ? "Entrar" : "Criar conta"}</h1>
          <p className="text-muted-foreground mt-1">{mode === "forgot" ? "Informe seu e-mail para receber o link de recuperação." : "Olá! Vamos criar seu primeiro documento?"}</p>

          {mode !== "forgot" && (<>
          <button onClick={googleLogin} data-testid="google-login-btn"
            className="mt-6 w-full h-12 rounded-xl border border-border bg-card hover:bg-accent transition-colors flex items-center justify-center gap-3 font-medium">
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 4.75c1.61 0 3.06.55 4.2 1.64l3.15-3.15C17.45 1.42 14.97.42 12 .42A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75Z"/></svg>
            Continuar com Google
          </button>

          <div className="flex items-center gap-3 my-6"><div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">ou com e-mail</span><div className="h-px flex-1 bg-border" /></div>
          </>)}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label>Nome</Label>
                <div className="relative mt-1"><User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input required value={form.name} onChange={set("name")} placeholder="Seu nome" className="pl-10 h-12" data-testid="register-name-input" /></div>
              </div>
            )}
            <div>
              <Label>E-mail</Label>
              <div className="relative mt-1"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input required type="email" value={form.email} onChange={set("email")} placeholder="voce@email.com" className="pl-10 h-12" data-testid="email-input" /></div>
            </div>
            {mode !== "forgot" && (
            <div>
              <Label>Senha</Label>
              <div className="relative mt-1"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input required type="password" value={form.password} onChange={set("password")} placeholder="••••••" className="pl-10 h-12" data-testid="password-input" /></div>
            </div>
            )}
            <Button type="submit" disabled={loading} className="w-full h-12 text-base" data-testid="submit-auth-btn">
              {loading ? "Aguarde..." : mode === "forgot" ? "Enviar link de recuperação" : mode === "login" ? "Entrar" : "Criar conta"}
            </Button>
            {mode === "login" && (
              <button type="button" onClick={() => setMode("forgot")} className="w-full text-center text-sm text-primary font-medium" data-testid="forgot-password-btn">Esqueci minha senha</button>
            )}
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {mode === "login" ? "Não tem conta?" : "Já tem conta?"}{" "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-primary font-semibold" data-testid="toggle-mode-btn">
              {mode === "login" ? "Criar conta grátis" : "Entrar"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
